/**
 * @file Pub Matching Reducer.
 */

import {
  PUBMATCHING_FETCH_VOTES_REQUEST,
  PUBMATCHING_FETCH_VOTES_SUCCESS,
  PUBMATCHING_VOTES_CAST_SUCCESS,
  PUBMATCHING_VOTE_CAST,
  PUBMATCHING_VOTE_ADD_PUBBOX,
  PUBMATCHING_VOTE_UPDATE_PUBBOX,
  PUBMATCHING_VOTE_DELETE_PUBBOX,
  PUBMATCHING_VOTES_CAST_FAILURE,
} from '../actions/pub-matching-actions';
import { COMMAND_PUB_MATCHING_VOTE_STATUS } from '../../constants/command';
import {
  QUEUE_EXTEND_LEASES,
  QUEUE_DROP_LEASES,
} from '../actions/queue-actions';
import { extendLeaseBatch } from '../../utilities/leasing';

const initialState = {
  queue: [],
  loading: false,
  error: null,
};

/**
 * Helper factory for creating vote batches for a pub matching queue. This is to
 * ensure consistency and provide data schema clarity.
 *
 * @param {Object} batch - The batch.
 * @returns {{leaseId: string, expiresAt: number, number: number, votes: Array,}}
 */
const createVoteBatch = batch => ({
  // All batches have this info. leaseId can be shared between different batches.
  leaseId: batch.leaseId,
  expiresAt: batch.expiresAt,
  votes: batch.votes,
  album: batch.album,
});

const pubMatchingReducer = (state = initialState, action = {}) => {
  switch (action.type) {
    case PUBMATCHING_FETCH_VOTES_REQUEST:
      return {
        ...state,
        loading: true,
      };

    case PUBMATCHING_FETCH_VOTES_SUCCESS: {
      const lease = createVoteBatch({
        leaseId: action.lease.lease_id,
        expiresAt: action.lease.expiresAt,
        votes: action.lease.votes,
        album: action.lease.album,
      });

      return {
        ...state,
        loading: false,
        queue: [lease, ...state.queue],
      };
    }

    case PUBMATCHING_VOTE_ADD_PUBBOX: {
      const { voteId, pubBox } = action;
      const [batch, ...rest] = state.queue;

      const updatedBatch = {
        ...batch,
        votes: batch.votes.map(vote => {
          if (vote.id === voteId) {
            return {
              ...vote,
              status: COMMAND_PUB_MATCHING_VOTE_STATUS.NUMBER_FOUND,
              pub_boxes: {
                ...vote.pub_boxes,
                records: [...vote.pub_boxes.records, pubBox],
              },
            };
          }

          return vote;
        }),
      };

      return {
        ...state,
        queue: [updatedBatch, ...rest],
      };
    }

    case PUBMATCHING_VOTE_UPDATE_PUBBOX: {
      const { voteId, pubBox } = action;
      const [batch, ...rest] = state.queue;

      const updatedBatch = {
        ...batch,
        votes: batch.votes.map(vote => {
          if (vote.id === voteId) {
            return {
              ...vote,
              status: COMMAND_PUB_MATCHING_VOTE_STATUS.NUMBER_FOUND,
              pub_boxes: {
                ...vote.pub_boxes,
                records: [
                  ...vote.pub_boxes.records.filter(pub => pub.id !== pubBox.id),
                  pubBox,
                ],
              },
            };
          }

          return vote;
        }),
      };

      return {
        ...state,
        queue: [updatedBatch, ...rest],
      };
    }

    case PUBMATCHING_VOTE_DELETE_PUBBOX: {
      const { voteId, pubBox } = action;
      const [batch, ...rest] = state.queue;

      const updatedBatch = {
        ...batch,
        votes: batch.votes.map(vote => {
          if (vote.id === voteId) {
            return {
              ...vote,
              // This might not be accurate, since we might be removing
              // all pub boxes, but we can at least assume that the user voted
              // related to numbers found or match errors.
              status: COMMAND_PUB_MATCHING_VOTE_STATUS.NUMBER_FOUND,
              pub_boxes: {
                ...vote.pub_boxes,
                records: vote.pub_boxes.records.filter(
                  pub => pub.id !== pubBox.id,
                ),
              },
            };
          }

          return vote;
        }),
      };

      return {
        ...state,
        queue: [updatedBatch, ...rest],
      };
    }

    case PUBMATCHING_VOTE_CAST: {
      const { voteId, status } = action;
      const [batch, ...rest] = state.queue;

      const updatedBatch = {
        ...batch,
        votes: batch.votes.map(vote => {
          if (vote.id === voteId) {
            return {
              ...vote,
              status,
            };
          }

          return vote;
        }),
      };

      return {
        ...state,
        queue: [updatedBatch, ...rest],
      };
    }

    case PUBMATCHING_VOTES_CAST_SUCCESS: {
      const [head, ...rest] = state.queue;

      const voteIds = action.votes.map(({ id }) => id);
      const newHead = {
        ...head,
        votes: head.votes.filter(vote => !voteIds.includes(vote.id)),
      };

      // If the head of the main queue is empty of votes discard it, otherwise
      // filter out the matched votes.
      const queue = newHead.votes.length === 0 ? rest : [newHead, ...rest];

      return {
        ...state,
        queue,
      };
    }

    case PUBMATCHING_VOTES_CAST_FAILURE: {
      return {
        ...state,
        error: action.error,
      };
    }

    case QUEUE_EXTEND_LEASES: {
      return {
        ...state,
        queue: state.queue.map(batch => extendLeaseBatch(batch, action)),
      };
    }

    case QUEUE_DROP_LEASES: {
      return {
        ...state,
        queue: state.queue.filter(
          batch => !action.leaseIds.includes(batch.leaseId),
        ),
      };
    }

    default:
      return state;
  }
};

export { initialState };
export default pubMatchingReducer;
